import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { api, RequestError, type VolunteerInput, type VolunteerProfile } from '../../lib/api'

export function VolunteersPage({ canManage }: { canManage: boolean }) {
  const client = useQueryClient()
  const volunteers = useQuery({ queryKey: ['volunteers'], queryFn: api.volunteers })
  const [editing, setEditing] = useState<VolunteerProfile | null>(null)
  const [name, setName] = useState('')
  const [abilities, setAbilities] = useState('')
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const reset = () => { setEditing(null); setName(''); setAbilities('') }
  const save = useMutation({
    mutationFn: ({ id, input }: { id: number | null; input: VolunteerInput }) =>
      id === null ? api.createVolunteer(input) : api.updateVolunteer(id, input),
    onSuccess: () => {
      reset()
      setNotice('اطلاعات داوطلب ذخیره شد.')
      void client.invalidateQueries({ queryKey: ['volunteers'] })
    },
  })
  const remove = useMutation({
    mutationFn: api.deleteVolunteer,
    onSuccess: (_, id) => {
      setDeleting(null)
      if (editing?.id === id) reset()
      setNotice('داوطلب حذف شد.')
      void client.invalidateQueries({ queryKey: ['volunteers'] })
    },
  })
  const pending = save.isPending || remove.isPending
  const error = save.error ?? remove.error ?? volunteers.error
  const rows = (volunteers.data?.volunteers ?? []).filter((v) =>
    `${v.name} ${v.abilities}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!abilities.trim() || pending) return
    setNotice(null)
    save.reset()
    remove.reset()
    save.mutate({ id: editing?.id ?? null, input: { name: name.trim() || 'volunteer1', abilities: abilities.trim() } })
  }

  return (
    <div className="container stack">
      <div className="card">
        <h1>داوطلبان</h1>
        <p className="meta">اینجا ثبت کنید هر داوطلب چه کمکی می‌تواند به مؤسسه بکند. افرا هم می‌تواند در گفت‌وگو داوطلب مناسب را پیدا کند.</p>
        <p className="meta">نام واقعی لازم نیست. نام مستعار مثل volunteer1 کافی است. فقط مهارت‌ها و نوع همکاری را بنویسید.</p>
      </div>
      {error && <p className="notice notice--error" role="alert">{error instanceof RequestError ? error.messageFa : 'دریافت یا ذخیره اطلاعات انجام نشد. دوباره تلاش کنید.'}</p>}
      {notice && <p className="notice notice--teach" role="status">{notice}</p>}
      {canManage && (
        <section className="card">
          <h2>{editing ? 'ویرایش داوطلب' : 'افزودن داوطلب'}</h2>
          <form className="stack" onSubmit={submit}>
            <div className="field">
              <label htmlFor="volunteer-name">نام یا نام مستعار (اختیاری)</label>
              <input id="volunteer-name" value={name} maxLength={200} placeholder="volunteer1" disabled={pending} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="volunteer-abilities">مهارت‌ها و نحوه همکاری</label>
              <textarea id="volunteer-abilities" value={abilities} maxLength={4000} rows={4} required disabled={pending} placeholder="مثلاً حسابداری، طراحی پوستر یا آموزش کودکان، هفته‌ای دو ساعت" onChange={(e) => setAbilities(e.target.value)} />
            </div>
            <div className="row">
              <button className="btn" type="submit" disabled={pending || !abilities.trim()}>{save.isPending ? 'در حال ذخیره…' : 'ذخیره داوطلب'}</button>
              {editing && <button className="btn btn--quiet" type="button" disabled={pending} onClick={() => { reset(); save.reset() }}>انصراف</button>}
            </div>
          </form>
        </section>
      )}
      <section className="card stack">
        <h2>فهرست داوطلبان</h2>
        <div className="field">
          <label htmlFor="volunteer-search">جست‌وجو در نام و مهارت‌ها</label>
          <input id="volunteer-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {volunteers.isLoading && <p className="muted" role="status">در حال بارگذاری…</p>}
        {volunteers.isError && <button className="btn btn--ghost" type="button" onClick={() => void volunteers.refetch()}>دوباره تلاش کن</button>}
        {volunteers.data && rows.length === 0 && <p className="muted">{search ? 'داوطلبی با این نام یا مهارت پیدا نشد.' : 'هنوز داوطلبی ثبت نشده.'}</p>}
        {rows.map((v) => (
          <article className="card stack" key={v.id}>
            <h3>{v.name}</h3>
            <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{v.abilities}</p>
            {canManage && <div className="row">
              <button className="btn btn--ghost btn--sm" type="button" disabled={pending} onClick={() => { setEditing(v); setName(v.name); setAbilities(v.abilities); setNotice(null); save.reset(); remove.reset() }}>ویرایش</button>
              <button className="btn btn--quiet btn--sm" type="button" disabled={pending} onClick={() => { setDeleting(v.id); remove.reset() }}>حذف</button>
            </div>}
            {canManage && deleting === v.id && <div className="notice notice--info">
              <p>داوطلب «{v.name}» حذف شود؟</p>
              <div className="row">
                <button className="btn btn--sm" type="button" disabled={pending} onClick={() => remove.mutate(v.id)}>{remove.isPending ? 'در حال حذف…' : 'بله، حذف کن'}</button>
                <button className="btn btn--quiet btn--sm" type="button" disabled={pending} onClick={() => setDeleting(null)}>انصراف</button>
              </div>
            </div>}
          </article>
        ))}
      </section>
    </div>
  )
}
